import { useGetEnrollmentMultiBankTransferData, useSubmitEnrollmentMultiBankTransferData } from '@egw/api'
import { Alert, AlertType, Button, ButtonVariant, GenericError, Input, Link, ModalDrawer } from '@egw/components'
import { configClient } from '@egw/config'
import { DEFAULT_CURRENCY_CODE, type FeatureFlags, WEALTH_ROUTES } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { Trans, useTranslation } from '@egw/i18n'
import { EnrollmentMultiLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { addCentsToCurrency, formatInputToCurrency, removeCurrencySymbols } from '@egw/utils'
import { useCallback, useEffect, useMemo, useState, type FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { useFlags } from '@egw/feature-flags'
import { ManualLink, PageHeader, PlaidLink } from '../../../components'
import { type Config } from '../../../config/config'
import { ALL_ENROLLMENT_ROUTES } from '../../../constants'
import { LinkingType } from '../../../types/account-type'

const log = new Logger('Enrollment - Multi - BankTransfer')

interface FormData {
  amount: string
  from: string
}

export const BankTransferPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()
  const config = configClient.getConfig() as Config
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [linkingType, setLinkingType] = useState<LinkingType>(LinkingType.NOT_SET)
  const [linkedAccountName, setLinkedAccountName] = useState<string | null>(null)
  const [transferError, setTransferError] = useState<boolean>(false)
  const { flags } = useFlags<FeatureFlags>()

  const { data, isLoading, error } = useGetEnrollmentMultiBankTransferData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentMultiBankTransferData()

  useEffect(() => {
    if (data?.enrollmentData?.fundingAccountName) {
      setLinkedAccountName(data.enrollmentData.fundingAccountName)
    }
  }, [data])

  const applicationId = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.applicationId : null

  useEffect(() => {
    if (!applicationId) return
    sendEvent('bankmulti_enroll_funding_started', { applicationId })
  }, [applicationId])

  const unlinkAccount = useCallback(async () => {
    sendEvent('bankmulti_enroll_unlink_account', { applicationId })

    setIsOpen(true)

    const unlinkResponse = await mutate({ unlinkAccount: true })

    if (unlinkResponse?.__typename === 'SubmitEnrollmentSuccess') {
      setLinkedAccountName(null)
    }
  }, [mutate, applicationId])

  const toInputHandler = async () => {
    if (linkedAccountName) {
      await unlinkAccount()
    } else {
      setIsOpen(true)
    }
  }

  // Protect route
  // Disabled to allow for FE navigation - BE can't navigate these child routes right now
  // useValidateRoute(data?.path)

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors: formErrors, isValid },
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      amount: '',
      from: '',
    },
  })

  const amountValue = watch('amount')

  useEffect(() => {
    setValue('from', linkedAccountName ?? '', { shouldValidate: false })
  }, [linkedAccountName, setValue])

  const fundAnotherWay = async (): Promise<void> => {
    void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.MULTI.ROUTES.FUNDING.path}`)
  }

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData
    if (isValid) {
      sendEvent('bankmulti_enroll_funding_submit', { applicationId })
      setTransferError(false)
      try {
        const fundingAmount = {
          amount: removeCurrencySymbols(formData.amount),
          currencyCode: DEFAULT_CURRENCY_CODE,
        }

        const achAuthTermsArray = config.zendeskArticles.achAuthorization.split('/')
        const id = `${achAuthTermsArray[achAuthTermsArray.length - 2]}/${achAuthTermsArray[achAuthTermsArray.length - 1]}`

        const response = await mutate({
          achAuthorization: {
            accepted: true,
            id,
          },
          fundingAmount,
          initiateFundsTransfer: true,
        })
        if (response?.__typename === 'SubmitEnrollmentSuccess' && response.enrollmentData?.transferFundsInitiated) {
          sendEvent('bankmulti_enroll_account_transfer_accepted')
          void navigate(
            `${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.MULTI.ROUTES.BANK_TRANSFER_SUCCESS.path}`,
          )
        } else {
          sendEvent('bankmulti_enroll_account_transfer_rejected')
          setTransferError(true)
        }
      } catch (e) {
        log.error('Error submitting form', e)
        setTransferError(true)
      }
    }
  })

  // Add cents to amount if they are missing
  const addCents = (): void => {
    sendEvent('bankmulti_enroll_funding_intent', { applicationId })

    const value = getValues('amount')

    if (value) {
      setValue('amount', addCentsToCurrency(value), { shouldValidate: true })
    }
  }

  const onClose = useCallback(() => {
    sendEvent('bankmulti_fundAccountModal_closed', { applicationId })
    setLinkingType(LinkingType.NOT_SET)
    setIsOpen(false)
  }, [applicationId])

  const modalTitle = useMemo((): string => {
    if (linkedAccountName) {
      return t('multi.bank_transfer.account_linked')
    }

    if (linkingType === LinkingType.MANUAL) {
      return t('multi.bank_transfer.manually_link_account')
    }

    return t('multi.bank_transfer.link_funding_account')
  }, [linkedAccountName, linkingType, t])

  // Determine which description to use based on feature flag
  const descriptionKey = flags.rolloutJointEnrollmentV1
    ? 'multi.bank_transfer.description_joint_enrollment'
    : 'multi.bank_transfer.description'

  return (
    <EnrollmentMultiLayout title={t('multi.bank_transfer.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <>
          <form onSubmit={(e) => void onSubmit(e)} className="w-full flex-1 flex flex-col justify-start">
            <PageHeader className="!mb-2xl" title={t('multi.bank_transfer.title')} />
            <p className="typo-primary-lg text-text-header mb-md">{t('multi.bank_transfer.subtitle')}</p>
            <p className="typo-primary-base text-text-meta mb-2xl">{t(descriptionKey)}</p>

            <div className="flex flex-col gap-sm">
              <Input
                disabled
                label={t('multi.bank_transfer.to_label')}
                value={t('multi.bank_transfer.to_value')}
                testId="to_input"
              />
              <Input
                id="from"
                value={linkedAccountName ?? ''}
                onFocus={() => void toInputHandler()}
                label={t('multi.bank_transfer.from_label')}
                readOnly
                icon={
                  <p className="typo-primary-base-semibold text-text-action cursor-pointer">
                    {t(linkedAccountName ? 'multi.bank_transfer.relink_account' : 'multi.bank_transfer.link_account')}
                  </p>
                }
                className="cursor-pointer"
                testId="btnLinkAccountModal_button"
              />
              <Controller
                name="from"
                control={control}
                render={({ field }) => <input type="hidden" {...field} value={field.value as unknown as string} />}
              />

              <Controller
                name="amount"
                control={control}
                rules={{
                  required: t('common.required_field'),
                  validate: {
                    maxAllowed: (value: string) => {
                      return (value && removeCurrencySymbols(value) <= 100000) || t('multi.bank_transfer.max_amount')
                    },
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    label={t('multi.bank_transfer.amount_label')}
                    placeholder={amountValue ? undefined : t('multi.bank_transfer.amount_placeholder')}
                    required
                    error={formErrors.amount?.message}
                    valueFormatter={(value) => formatInputToCurrency(value, true)}
                    onBlur={addCents}
                    inputMode="decimal"
                    testId="amount_input"
                    className="cursor-pointer"
                  />
                )}
              />
            </div>

            {submitError ? <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" /> : null}

            {/* Transfer Error */}
            {transferError ? (
              <Alert alertType={AlertType.Error} description={t('multi.bank_transfer.errors.fund_transfer')} className="mt-sm" />
            ) : null}

            {/* AUTO GENERATED COMPONENTS */}

            <div className="text-text-meta typo-primary-base mt-2xl">
              <Trans
                className="typo-primary-base text-text-body"
                t={t}
                i18nKey="multi.bank_transfer.ach_terms_and_conditions"
              >
                <Link
                  href={config.zendeskArticles.achAuthorization}
                  className="hyperlink-muted underline inline-flex typo-primary-base-semibold text-text-body hover:text-text-body inline"
                />
              </Trans>
            </div>

            <div className="flex flex-col sm:flex-row gap-md mt-xl">
              <Button
                variant={ButtonVariant.Secondary}
                onClick={() => {
                  void fundAnotherWay()
                }}
                className="w-full md:w-min"
                testId="skipFundingAction_button"
              >
                {t('multi.bank_transfer.fund_another_way')}
              </Button>

              <Button
                type="submit"
                className="w-full md:w-min"
                isDisabled={!isValid}
                isLoading={isSubmitting}
                testId="primaryAction_button"
              >
                {t('multi.bank_transfer.transfer_funds')}
              </Button>
            </div>
          </form>

          <ModalDrawer isOpen={isOpen} onClose={onClose} title={modalTitle}>
            {isOpen && linkingType !== LinkingType.MANUAL ? (
              <PlaidLink
                applicationId={applicationId}
                setLinkingType={setLinkingType}
                onPlaidSuccess={setLinkedAccountName}
                onClose={onClose}
              />
            ) : null}

            {linkingType === LinkingType.NOT_SET && (
              <div className="border-t border-light flex justify-center mt-md mb-2xs">
                <span className="text-meta bg-white -mt-3 px-3">or</span>
              </div>
            )}

            {linkingType !== LinkingType.PLAID_INSTANT ? (
              <ManualLink
                applicationId={applicationId}
                onAccountLinkSuccess={setLinkedAccountName}
                setLinkingType={setLinkingType}
                onClose={onClose}
              />
            ) : null}
          </ModalDrawer>
        </>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentMultiLayout>
  )
}
